














































































































































































































import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  Ref,
} from "@vue/composition-api";
import { required, email } from "@vuelidate/validators";
import { useMachine } from "@xstate/vue";
import Router from "@/router";
import Store from "@/store";
import useVuelidate from "@vuelidate/core";

import { AuthenticationApi, AuthorizedResultDto, MaintenanceApi } from "@/api";
import { AuthState } from "@/store/modules/auth/types";
import { mapValidationErrors } from "@/util/Forms";
import LoginMachine from "./LoginMachine";

// This should be a global form, used by all validatable forms
interface GenericForm {
  errors: {};
  validations: typeof useVuelidate;
}

interface LoginForm extends GenericForm {
  fields: {
    emailAddress: Ref<string>;
    password: Ref<string>;
    pin: Ref<string>;
  };
}

const Component = defineComponent({
  name: "Login",
  setup(props, vm) {
    const emailAddress = ref("");
    const password = ref("");
    const pin = ref("");
    const qrCode = ref<string | null | undefined>("");

    const { state, send, service } = useMachine(LoginMachine);
    const api = new AuthenticationApi();
    const maintenanceApi = new MaintenanceApi();

    const isMfa = computed(() => state.value.context.mfaEnabled || state.value.matches('setupMfa'))

    const logoutTrigger = ref(vm.root.$route.query.trigger);

    const rules = {
      emailAddress: {
        required,
        email,
        $autoDirty: true,
      },
      password: {
        required,
        $autoDirty: true,
      },
      pin: {
        required: (value: string | number) => {
          if (isMfa.value) {
            return required.$validator(value);
          }
          return true;
        },
        $autoDirty: true,
      },
    }

    const form = reactive<LoginForm>({
      errors: {},

      fields: {
        emailAddress,
        password,
        pin,
      },

      validations: useVuelidate(
        rules,
        { emailAddress, password, pin }
      ),
    });

    const announcementSnackbar = ref(false);
    const announcementTitle = ref("");
    const announcementContent = ref("");

    async function ping() {
      // This triggers the under construction page.
      await maintenanceApi.maintenancePing();
    }

    async function getAnnouncement() {
      try {
        const { data } = await maintenanceApi.maintenanceGetAnnouncementText();
        if (data.content) {
          announcementTitle.value = data.title || "";
          announcementContent.value = data.content || "";
          announcementSnackbar.value = true;
        }
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }

    async function logIn() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await api.authenticationLoginWithCredentials({
          emailAddress: form.fields.emailAddress,
          password: form.fields.password,
        });
        const authState: AuthState = {
          user: {
            email: form.fields.emailAddress,
          },
          authorization: response.data,
        };
        if (response.response && response.response.status === 503) {
          // In case of 503, redirect to construction page
          Router.push({ name: "underconstruction" });
        } else {
          Store.dispatch("Auth/logIn", authState).then(() => {
            Router.push({ name: "dashboard" });
          });
        }
      } catch (err) {
        if (err.response) {
          // 409 = 2fa is enabled in your account, proceed to enter 2fa token
          if (err.response.status === 409) {
            service.send("MFA");
          }
          // 422 = 2fa is enabled for your project, but not set for your account. Proceed to setup 2fa
          else if (err.response.status === 422) {
            const { data } = await api.authenticationGetTotpSetupCode({
              emailAddress: form.fields.emailAddress,
              password: form.fields.password,
            });

            qrCode.value = data.qrSetupCodeImage;

            service.send("MFA_SETUP");
          } else {
            service.send("REJECT");
          }
        } else {
          service.send("REJECT");
        }
      }
    }

    async function logInWithTotp() {
      try {
        const { data } = await api.authenticationLoginWithTotp({
          emailAddress: form.fields.emailAddress,
          password: form.fields.password,
          pin: form.fields.pin,
        });
        const authState: AuthState = {
          user: {
            email: form.fields.emailAddress,
          },
          authorization: data,
        };
        Store.dispatch("Auth/logIn", authState).then(() => {
          Router.push({ name: "dashboard" });
        });
      } catch (err) {
        service.send("REJECTMFA");
      }
    }

    async function handleSubmitSetupMfaPin() {
      try {
        await api.authenticationEnableTotp({
          emailAddress: form.fields.emailAddress,
          password: form.fields.password,
          pin: form.fields.pin,
        });

        resetValidations()

        service.send("LOGIN");
      } catch (error) {
        form.errors = (error as any).response.data.errors;
      }
    }

    function resetValidations() {
      form.validations.emailAddress.$reset()
      form.validations.password.$reset()
      form.validations.pin.$reset()

      form.fields.emailAddress = '';
      form.fields.password = '';
      form.fields.pin = '';
    }


    onMounted(() => {
      ping();
      getAnnouncement();

      service.subscribe((s) => {
        if (s.matches("loading")) {
          logoutTrigger.value = "";
          if (s.context.mfaEnabled) {
            logInWithTotp();
          } else {
            logIn();
          }
        }
      });
    });

    return {
      announcementContent,
      announcementSnackbar,
      announcementTitle,
      form,
      isMfa,
      logoutTrigger,
      qrCode,
      service,
      state,
      handleSubmitSetupMfaPin,
      mapValidationErrors,
      send,
      resetValidations,
    };
  },
});

export default Component;
